// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { getWallets } from "@wallet-standard/app";
import type { Wallet } from "@wallet-standard/base";
import type { FrostConfig } from "../config";
import type { WalletEntity } from "../types";

const RIALO_CHAIN_PREFIX = "rialo:";

export class WalletRegistry {
  private readonly config: FrostConfig;
  private unsubscribe?: () => void;
  private readyPromise: Promise<void>;
  private readyResolve!: () => void;

  public constructor(config: FrostConfig) {
    this.config = config;
    this.readyPromise = new Promise(resolve => {
      this.readyResolve = resolve;
    });
    this.initialize();
  }

  private initialize(): void {
    // SSR guard
    if (typeof window === "undefined") {
      this.readyResolve();
      return;
    }
    const { get, on } = getWallets();

    // Register existing wallets
    const existing = get();
    if (existing.length > 0) {
      let hasRialoWallet = false;
      for (const wallet of existing) {
        if (this.registerWallet(wallet)) {
          hasRialoWallet = true;
        }
      }
      if (hasRialoWallet) {
        this.readyResolve();
      }
    }

    // Listen for wallet registration/unregistration events
    const unsubscribeRegister = on("register", wallet => {
      if (this.registerWallet(wallet)) {
        this.readyResolve();
      }
    });

    const unsubscribeUnregister = on("unregister", wallet => {
      this.unregisterWallet(wallet.name);
    });

    this.unsubscribe = (): void => {
      unsubscribeRegister();
      unsubscribeUnregister();
    };

    // Resolve ready after timeout if no wallets found (allows app to proceed)
    setTimeout(() => this.readyResolve(), 1000);
  }

  /**
   * Register a wallet if it supports Rialo chains
   * @returns true if wallet was registered (supports Rialo)
   */
  private registerWallet(wallet: Wallet): boolean {
    // Only register Rialo-compatible wallets
    if (!wallet.chains.some(c => c.startsWith(RIALO_CHAIN_PREFIX))) {
      return false;
    }

    const entity: WalletEntity = {
      name: wallet.name,
      icon: wallet.icon,
      chains: [...wallet.chains],
      features: Object.keys(wallet.features),
      installedVersion: wallet.version,
    };

    this.config.store.setState(state => {
      const wallets = new Map(state.wallets);
      const existing = wallets.get(wallet.name);

      // Preserve user data like lastConnectedAt and priority
      wallets.set(wallet.name, {
        ...entity,
        lastConnectedAt: existing?.lastConnectedAt,
        priority: existing?.priority,
      });

      const _walletRefs = new Map(state._walletRefs);
      _walletRefs.set(wallet.name, wallet);

      return { ...state, wallets, _walletRefs };
    });

    return true;
  }

  private unregisterWallet(walletName: string): void {
    this.config.store.setState(state => {
      const wallets = new Map(state.wallets);
      wallets.delete(walletName);

      const _walletRefs = new Map(state._walletRefs);
      _walletRefs.delete(walletName);

      // If this was the connected wallet, disconnect
      const shouldDisconnect = state.walletName === walletName;

      return {
        ...state,
        wallets,
        _walletRefs,
        ...(shouldDisconnect && {
          status: "disconnected" as const,
          walletName: null,
          accountAddress: null,
          connectedAt: null,
          sessionExpiresAt: null,
        }),
      };
    });
  }

  /** Wait for a specific wallet to be discovered */
  public waitForWallet(name: string, options?: { timeout?: number }): Promise<Wallet | null> {
    const timeout = options?.timeout ?? 5000;

    // Check if already discovered
    const existing = this.config.store.state._walletRefs.get(name);
    if (existing) return Promise.resolve(existing);

    return new Promise(resolve => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        resolve(null);
      }, timeout);

      const unsubscribe = this.config.store.subscribe(({ currentVal }) => {
        const wallet = currentVal._walletRefs.get(name);
        if (wallet) {
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(wallet);
        }
      });
    });
  }

  /** Promise that resolves when at least one Rialo wallet is discovered */
  public get ready(): Promise<void> {
    return this.readyPromise;
  }

  /** Check if any wallets have been discovered */
  public get isReady(): boolean {
    return this.config.store.state.wallets.size > 0;
  }

  public destroy(): void {
    this.unsubscribe?.();
  }
}
